"use client";

import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Fire an analytics impression EXACTLY ONCE per logical presentation, no matter
 * how many times the component re-renders.
 *
 * Why this exists (2026-06 offer re-fire incident): the offer card's impression
 * was emitted from a `useEffect` keyed on OBJECT/ARRAY dependencies. When a
 * parent passed unstable references (a non-memoized `visibleChatProducts(result)`
 * array), the effect re-ran on every render and fired `agent_revenue_offer_shown`
 * 5–9× per user — inflating impressions, masking a real click collapse, and
 * flooding Sentry until its quota maxed (100% rejected). Memoizing the parent
 * props fixes the trigger, but the DURABLE fix is to make re-fire impossible by
 * construction: gate emission on a STABLE STRING key via a ref, never on object
 * identity. Every surface that records an impression should route through this
 * hook so the bug class cannot reappear one component at a time.
 *
 * @param key  Stable identity of the presentation, e.g.
 *             ``${planId}:${placement}:${sourcePath}``. Pass null/"" while not
 *             ready; emission waits until a real key arrives, then fires once.
 * @param emit Side effect to run once for that key. It is read from a ref, so a
 *             fresh closure each render is fine — it never re-triggers the effect.
 */
// Cross-remount dedup (rule 47). `useRef` guards re-fire WITHIN a single mount,
// but a component that UNMOUNTS and REMOUNTS (a streaming chat re-rendering its
// offer card, a virtualized list recycling it, a client-side nav back to the same
// presentation) resets the ref and fires the impression AGAIN for the same key —
// silently inflating `offer_seen` (chat surface measured 1.68 impr/user + ~26%
// <10s dups on 2026-07-13, deflating the reported chat offer CTR). This
// module-level set persists a fired key for the lifetime of the SPA session (it
// clears on a full page reload, which IS a new visit), so an impression fires
// EXACTLY ONCE per logical presentation across remounts. Opt-in (`persist`), and
// ONLY safe when the key is per-presentation unique (includes readingId /
// sourcePath / orderId) — a constant key would then fire once per session.
const firedPresentationKeys = new Set<string>();

/**
 * TEST-ONLY: clear the module-level cross-remount dedup set. The set intentionally
 * has no production reset (it lives for the SPA session), so tests that reuse a
 * presentation key across cases must call this in `beforeEach` to stay isolated.
 */
export function __resetImpressionDedupForTest(): void {
  firedPresentationKeys.clear();
}

function presentationAlreadyFired(
  key: string,
  persist: boolean,
  firedKeyRef: { current: string | null }
): boolean {
  if (firedKeyRef.current !== key) return false;
  if (persist) firedPresentationKeys.add(key);
  return true;
}

type ImpressionDelivery = void | boolean | PromiseLike<void | boolean>;

function commitVisibleImpression(
  key: string,
  persist: boolean,
  firedKeyRef: { current: string | null },
  pendingKeyRef: { current: string | null }
): void {
  firedKeyRef.current = key;
  if (pendingKeyRef.current === key) pendingKeyRef.current = null;
  if (persist) firedPresentationKeys.add(key);
}

function emitVisibleImpression(
  key: string,
  persist: boolean,
  emit: () => ImpressionDelivery,
  firedKeyRef: { current: string | null },
  pendingKeyRef: { current: string | null }
): void {
  if (firedKeyRef.current === key || pendingKeyRef.current === key) return;
  pendingKeyRef.current = key;
  const delivery = emit();
  if (!delivery || typeof delivery !== "object" || !("then" in delivery)) {
    if (delivery !== false) commitVisibleImpression(key, persist, firedKeyRef, pendingKeyRef);
    else if (pendingKeyRef.current === key) pendingKeyRef.current = null;
    return;
  }
  void Promise.resolve(delivery).then((delivered) => {
    if (delivered !== false) commitVisibleImpression(key, persist, firedKeyRef, pendingKeyRef);
    else if (pendingKeyRef.current === key) pendingKeyRef.current = null;
  });
}

export function useImpressionOnce(
  key: string | null | undefined,
  emit: () => void,
  opts?: { persist?: boolean }
): void {
  const persist = opts?.persist ?? false;
  const firedKeyRef = useRef<string | null>(null);
  const emitRef = useRef(emit);

  // Keep the latest emit in a ref via an effect (not during render) so a fresh
  // closure each render never re-triggers the fire effect below.
  useEffect(() => {
    emitRef.current = emit;
  });

  useEffect(() => {
    if (!key || presentationAlreadyFired(key, persist, firedKeyRef)) return;
    if (persist && firedPresentationKeys.has(key)) {
      firedKeyRef.current = key; // don't re-check across this mount's renders
      return;
    }
    firedKeyRef.current = key;
    if (persist) firedPresentationKeys.add(key);
    emitRef.current();
  }, [key, persist]);
}

/**
 * Returns true once `ready` is true, OR after `fallbackMs` elapses — whichever
 * comes first. Lets an impression WAIT for a decision/data fetch to resolve (so
 * it can log the right details) while GUARANTEEING it still fires if that fetch
 * is slow, cancelled (unstable-deps thrash), or never resolves. Pair with
 * `useImpressionOnce` + a stable constant key for a fire-exactly-once-but-never-
 * zero impression (rule 46/47): the gate that silently dropped ~90% of offer
 * impressions on 2026-06-14 (1,582 -> 156 users) was a readiness flag that
 * never flipped when the /recommend fetch didn't resolve. The timer starts on
 * mount; keep `fallbackMs` constant across renders.
 */
/**
 * Fire an impression EXACTLY ONCE, but only after the offer is GENUINELY SEEN —
 * ≥`threshold` of it visible in the viewport for ≥`dwellMs` continuously.
 *
 * Why this exists: `agent_revenue_offer_shown` previously fired on mount/render
 * (via `useImpressionOnce`), so a below-the-fold offer the seeker never scrolled
 * to still counted as "seen". That inflated the `offer_seen` denominator and made
 * the rule-46 offer_seen→click leading indicator read low for a measurement
 * reason, not a UX one. Gating on real visibility (IntersectionObserver) makes
 * `offer_seen` mean "the offer entered the seeker's view", so the click-through
 * rate is honest.
 *
 * Drop-safety (the 2026-06-14 lesson, rule 47): a visibility gate must never
 * silently drop EVERY impression. If there is no element yet, or the runtime has
 * no `IntersectionObserver` (SSR / very old browser), this FALLS BACK to firing
 * once on mount — preserving the prior behavior rather than going dark. A genuine
 * "never scrolled to it" non-fire is correct (the offer truly wasn't seen); only
 * an environment-capability gap falls back.
 *
 * @param ref  Ref to the offer's root element (the thing whose visibility counts).
 */
export function useImpressionWhenVisible(
  key: string | null | undefined,
  emit: () => ImpressionDelivery,
  ref: RefObject<Element | null>,
  opts?: { threshold?: number; dwellMs?: number; persist?: boolean }
): void {
  const threshold = opts?.threshold ?? 0.5;
  const dwellMs = opts?.dwellMs ?? 1000;
  const persist = opts?.persist ?? false;
  const firedKeyRef = useRef<string | null>(null);
  const pendingKeyRef = useRef<string | null>(null);
  const emitRef = useRef(emit);
  useEffect(() => {
    emitRef.current = emit;
  });

  useEffect(() => {
    if (!key || presentationAlreadyFired(key, persist, firedKeyRef)) return;
    // Cross-remount dedup (rule 47) — see `firedPresentationKeys` above. A visible
    // offer that unmounts (scrolled away / streamed re-render) and remounts must
    // not re-count as a fresh `offer_seen`. Opt-in; key must be presentation-unique.
    if (persist && firedPresentationKeys.has(key)) {
      firedKeyRef.current = key;
      return;
    }
    const fire = () => {
      emitVisibleImpression(key, persist, emitRef.current, firedKeyRef, pendingKeyRef);
    };

    const el = ref.current;
    // Capability fallback: no element to observe, or no IntersectionObserver
    // (SSR / legacy) — fire once on mount so we never go dark (rule 47).
    if (!el || typeof IntersectionObserver === "undefined") {
      fire();
      return;
    }

    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    const clearDwell = () => {
      if (dwellTimer) {
        clearTimeout(dwellTimer);
        dwellTimer = null;
      }
    };
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            if (!dwellTimer) dwellTimer = setTimeout(fire, dwellMs);
          } else {
            clearDwell();
          }
        }
      },
      { threshold: [threshold] }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearDwell();
    };
  }, [key, ref, threshold, dwellMs, persist]);
}

export function useSettled(ready: boolean, fallbackMs = 1200): boolean {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setElapsed(true), fallbackMs);
    return () => clearTimeout(timer);
  }, [fallbackMs]);
  return ready || elapsed;
}
